import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import boschBg from '../../media/navTopBar.png';
import logo from '../../media/bosch_logo.png';
import LanguageSelector from '../Header/LanguageSelector';
import axiosInstance from '../../utils/axiosInstance';
import i18n from '../../i18n';

const LegacyHeader = ({ userInfo, showCiscoArchive }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [portalLanguages, setPortalLanguages] = useState([]);

  useEffect(() => {
    const portalLanguagesURL = '/api/v1/snow/portal/languages';
    axiosInstance
      .get(portalLanguagesURL)
      .then((response) => {
        if (response.status === 200 && response.data.isSuccessful) {
          setPortalLanguages(response.data.data);
        }
      })
      .catch((error) => {
        console.error('Error loading portal languages:', error);
      });
  }, []);

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const myTasksUrl = process.env.REACT_APP_MY_TASKS_URL?.trim();
  const myTicketsUrl = process.env.REACT_APP_MY_TICKETS_URL?.trim();
  const myFavoritesUrl = process.env.REACT_APP_MY_FAVORITES_URL?.trim();
  const feedbackUrl = process.env.REACT_APP_FEEDBACK_URL?.trim();
  const homeUrl = process.env.REACT_APP_HOME_URL?.trim();
  const servicesUrl = process.env.REACT_APP_SERVICES_URL?.trim();
  const supportUrl = process.env.REACT_APP_SUPPORT_URL?.trim();
  const myAssetsUrl = process.env.REACT_APP_MY_ASSETS_FULL_URL?.trim();

  const userInitials = userInfo?.userInitials || '';
  const userFullName = userInfo
    ? `${userInfo.userGivenName} ${userInfo.userFamilyName}`
    : '';

  const handleLogout = () => {
    try {
      Cookies.remove('spear-sso');
    } catch (err) {
      // cookie eventually expires
    }
    window.location.href = `${process.env.REACT_APP_LOGOUT_URL}`;
  };

  const REQUESTED_ITEMS_URL = `${process.env.REACT_APP_SNOW_URL}/now/nav/ui/classic/params/target/sc_req_item_list.do%3Fsysparm_clear_stack%3Dtrue`;

  const secondaryNavLinks = [
    {
      title: t('ui_spear.lhdr.menu_home', 'Home'),
      href: homeUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.menu_services', 'Services'),
      href: servicesUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.menu_support', 'Support'),
      href: supportUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.menu_my_devices_new_v3', 'My Devices'),
      href: myAssetsUrl || undefined
    }
  ];

  const primaryNavLinks = [
    {
      title: t('ui_spear.lhdr.header_my_tasks', 'My Tasks'),
      href: myTasksUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.header_my_tickets', 'My Tickets'),
      href: myTicketsUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.header_my_favorites', 'Favorites'),
      href: myFavoritesUrl || undefined
    },
    {
      title: t('ui_spear.lhdr.header_feedback', 'Feedback'),
      href: feedbackUrl || undefined
    }
  ];

  return (
    <header className="w-full bg-white sticky top-0 z-50">
      {/* 1. Top Color Banner - existing navTopBar image */}
      <div
        className="w-full"
        style={{
          borderImage: `url(${boschBg}) 10%`,
          borderStyle: 'solid none none',
          borderWidth: '.8rem 0 0'
        }}
      />

      {/* 2. Primary Header Bar */}
      <div className="w-full py-1 px-1 flex items-center justify-between">
        {/* Left - Bosch Logo (existing) */}
        <div className="flex items-center">
          <img src={logo} alt="Bosch" className="h-16" />
        </div>

        {/* Right - User Menu */}
        <div className="flex items-center gap-6">
          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-8">
            {primaryNavLinks.map((link) => {
              const baseClassName =
                'font-Bosch_sans no-underline rounded-none text-black text-[1.15rem] transition-colors duration-150';

              if (link.href) {
                return (
                  <a
                    key={link.title}
                    href={link.href}
                    className={`${baseClassName} cursor-pointer hover:text-[#007bc0]`}
                  >
                    {link.title}
                  </a>
                );
              }

              return (
                <span
                  key={link.title}
                  className={`${baseClassName} cursor-default`}
                >
                  {link.title}
                </span>
              );
            })}
          </nav>

          {/* User Profile with Dropdown */}
          <div className="relative flex items-center gap-2 mr-4">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-0"
            >
              <div
                className="avatar-container flex items-center justify-center"
                style={{
                  width: '2.2rem',
                  height: '2.2rem',
                  borderRadius: '50%',
                  background: '#fff',
                  border: '1px solid #000',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  userSelect: 'none'
                }}
              >
                <span
                  className="font-Bosch_sans"
                  style={{
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    lineHeight: '2.2rem',
                    width: '100%',
                    textAlign: 'center',
                    display: 'block',
                    letterSpacing: '0.05em',
                    userSelect: 'none'
                  }}
                >
                  {userInitials}
                </span>
              </div>
              <span className="hidden lg:inline text-[1.15rem] text-[#333] font-Bosch_sans">
                {userFullName}
              </span>
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 min-w-[220px] bg-white rounded shadow-lg border border-gray-200 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="block text-[1.1rem] text-[#343d47] font-Bosch_sans mb-2">
                    {t('ui_spear.Profile.Language', 'Language')}
                  </span>
                  <LanguageSelector
                    userLanguage={userInfo?.userLanguage}
                    portalLanguages={portalLanguages}
                    handleLanguageChange={handleLanguageChange}
                  />
                </div>
                <a
                  href={REQUESTED_ITEMS_URL}
                  className="flex items-center gap-2 px-4 py-3 text-[1.1rem] text-[#343d47] no-underline hover:bg-gray-100 font-Bosch_sans"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  {t('ui_spear.Profile.Requested_Items', 'Requested Items')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Secondary Navigation Bar */}
      <div
        className="w-full px-6 py-4"
        style={{ borderBottom: '1px solid #e3e3e3' }}
      >
        <nav className="flex items-center gap-10">
          {secondaryNavLinks.map((link) => {
            const baseClassName =
              'font-Bosch_sans no-underline rounded-none text-black text-[1.15rem] transition-colors duration-150';

            if (link.href) {
              return (
                <a
                  key={link.title}
                  href={link.href}
                  className={`${baseClassName} cursor-pointer hover:text-[#007bc0]`}
                >
                  {link.title}
                </a>
              );
            }

            return (
              <span
                key={link.title}
                className={`${baseClassName} cursor-default`}
              >
                {link.title}
              </span>
            );
          })}
          {/* Cisco Archive - internal SPEAR route, shown only for users with CPSC/Cisco tickets */}
          {showCiscoArchive && (
            <Link
              to="/cisco/tickets"
              className="font-Bosch_sans no-underline rounded-none text-black text-[1.15rem] transition-colors duration-150 cursor-pointer hover:text-[#007bc0]"
            >
              {t('ui_spear.lhdr.menu_my_archived_requests', 'My Archived Requests')}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default LegacyHeader;
